import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { FundraisingCampaignService } from '@/services';
import { QueryParams } from '@/model';
import {
  CreateFundraisingCampaignPayload,
  UpdateFundraisingCampaignPayload,
  CreateFundraisingCampaignJustifyExpensesPayload,
  UpdateFundraisingCampaignJustifyExpensesPayload,
} from '../request/fundraising-campaign';
import { FundraisingCampaignConverter } from '@/converters/fundraising-campaign';

export const useFundraisingCampaign = (params?: QueryParams, enabled = true) => {
  const query = useQuery(
    ['GET_FUNDRAISING_CAMPAIGN', params],
    async () => {
      const [err, data] = await FundraisingCampaignService.getFundraisingCampaign(params);
      if (err) {
        throw err;
      }

      return {
        data: FundraisingCampaignConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
      enabled,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useFundraisingCampaignById = (id: string, enabled = true) => {
  const query = useQuery(
    ['GET_FUNDRAISING_CAMPAIGN_BY_ID', id],
    async () => {
      const [err, data] = await FundraisingCampaignService.getFundraisingCampaignById(id);
      if (err) {
        throw err;
      }
      return FundraisingCampaignConverter.toDomainFromGetItemResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const usePostFundraisingCampaignMutation = () => {
  return useMutation({
    mutationKey: ['POST_FUNDRAISING_CAMPAIGN'],
    mutationFn: async (payload: CreateFundraisingCampaignPayload) => {
      const [err, data] = await FundraisingCampaignService.postFundraisingCampaign(payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

type UsePutFundraisingCampaignMutationParams = {
  id: string;
  payload: UpdateFundraisingCampaignPayload;
};
export const usePutFundraisingCampaignMutation = () => {
  return useMutation({
    mutationKey: ['PUT_FUNDRAISING_CAMPAIGN'],
    mutationFn: async ({ id, payload }: UsePutFundraisingCampaignMutationParams) => {
      const [err, data] = await FundraisingCampaignService.putFundraisingCampaign(id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const usePublishFundraisingCampaignMutation = () => {
  return useMutation({
    mutationKey: ['PUBLISH_FUNDRAISING_CAMPAIGN'],
    mutationFn: async (id: string) => {
      const [err, data] = await FundraisingCampaignService.publishFundraisingCampaign(id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useEndFundraisingCampaignMutation = () => {
  return useMutation({
    mutationKey: ['END_FUNDRAISING_CAMPAIGN'],
    mutationFn: async (id: string) => {
      const [err, data] = await FundraisingCampaignService.endFundraisingCampaign(id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useDeleteFundraisingCampaignMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_FUNDRAISING_CAMPAIGN'],
    mutationFn: async (id: string) => {
      const [err, data] = await FundraisingCampaignService.deleteFundraisingCampaign(id);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

export const useFundraisingCampaignJustifyExpenses = (id: string, enabled = true) => {
  const query = useQuery(
    ['GET_FUNDRAISING_CAMPAIGN_JUSTIFY_EXPENSES'],
    async () => {
      const [err, data] = await FundraisingCampaignService.getFundraisingCampaignJustifyExpenses(id);
      if (err) {
        throw err;
      }

      return FundraisingCampaignConverter.toDomainFromGetJustifyExpensesResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      enabled,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

type UsePostFundraisingCampaignJustifyExpensesMutationParams = {
  id: string;
  payload: CreateFundraisingCampaignJustifyExpensesPayload;
};
export const usePostFundraisingCampaignJustifyExpensesMutation = () => {
  return useMutation({
    mutationKey: ['POST_FUNDRAISING_CAMPAIGN_JUSTIFY_EXPENSES'],
    mutationFn: async ({ id, payload }: UsePostFundraisingCampaignJustifyExpensesMutationParams) => {
      const [err, data] = await FundraisingCampaignService.postFundraisingCampaignJustifyExpenses(id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};

type UsePutFundraisingCampaignJustifyExpensesMutationParams = {
  id: string;
  payload: UpdateFundraisingCampaignJustifyExpensesPayload;
};
export const usePutFundraisingCampaignJustifyExpensesMutation = () => {
  return useMutation({
    mutationKey: ['PUT_FUNDRAISING_CAMPAIGN_JUSTIFY_EXPENSES'],
    mutationFn: async ({ id, payload }: UsePutFundraisingCampaignJustifyExpensesMutationParams) => {
      const [err, data] = await FundraisingCampaignService.putFundraisingCampaignJustifyExpenses(id, payload);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};
